import React, { useState } from 'react';
import { PRESET_STRUCTURE_TYPES } from '@/features/training/types';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Badge } from '@/shared/ui/badge';
import type { CreateStructureTypeFormData, CreateStructureTypeFormProps } from './types';

export const CreateStructureTypeForm: React.FC<CreateStructureTypeFormProps> = ({
  existingStructureTypeNames = [],
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreateStructureTypeFormData>({
    name: '',
    is_preset: false,
  });
  const [useCustom, setUseCustom] = useState(false);
  const normalizedExistingNames = new Set(
    existingStructureTypeNames.map((name) => name.trim().toLowerCase()),
  );
  const isDuplicateName = normalizedExistingNames.has(formData.name.trim().toLowerCase());

  const handlePresetSelect = (preset: string) => {
    const isPresetAlreadyCreated = normalizedExistingNames.has(preset.trim().toLowerCase());
    if (isPresetAlreadyCreated) return;
    setFormData({ name: preset, is_preset: true });
    setUseCustom(false);
  };

  const handleCustomToggle = () => {
    setUseCustom(true);
    setFormData({ name: '', is_preset: false });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || isDuplicateName) return;
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Select a preset type or create your own</Label>
        <div className="flex flex-wrap gap-2">
          {PRESET_STRUCTURE_TYPES.map((preset) => (
            (() => {
              const isExisting = normalizedExistingNames.has(preset.trim().toLowerCase());
              return (
                <Badge
                  key={preset}
                  variant={formData.name === preset && !useCustom ? 'default' : 'outline'}
                  className={`select-none py-1.5 px-3 text-sm ${
                    isExisting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  }`}
                  onClick={() => handlePresetSelect(preset)}
                >
                  {preset}
                </Badge>
              );
            })()
          ))}
          <Badge
            variant={useCustom ? 'default' : 'outline'}
            className="cursor-pointer select-none py-1.5 px-3 text-sm"
            onClick={handleCustomToggle}
          >
            + Custom
          </Badge>
        </div>
      </div>

      {useCustom && (
        <div className="space-y-2">
          <Label htmlFor="structure-type-name">Custom type name</Label>
          <Input
            id="structure-type-name"
            placeholder="e.g. Mixed-Use, Government..."
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            autoFocus
          />
        </div>
      )}

      {formData.name && !useCustom && (
        <p className="text-sm text-muted-foreground">
          Selected: <span className="font-medium text-foreground">{formData.name}</span>
        </p>
      )}
      {isDuplicateName && (
        <p className="text-sm text-amber-700">
          This structure type already exists for your client. Choose another option.
        </p>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || !formData.name.trim() || isDuplicateName}>
          {isLoading ? 'Creating...' : 'Create Structure Type'}
        </Button>
      </div>
    </form>
  );
};
